import React, { useState, useEffect, ReactElement } from 'react';
import styled, { keyframes } from 'styled-components';
import { motion } from 'framer-motion';
import { FaSpinner, FaCheckCircle, FaCreditCard, FaRupeeSign } from 'react-icons/fa';
import { IconBaseProps } from 'react-icons';
import {
  FormContainer,
  FormTitle,
  FormGroup,
  FormRow,
  Label,
  Input,
  Select,
  Button,
  ErrorMessage,
  PaymentContainer,
  PaymentTitle,
  PaymentButton,
  ConfirmationMessage,
  PhoneInputWrapper,
  PhonePrefix,
  PaymentSteps,
  Step,
  PriceTag,
  PriceList,
  PriceItem,
  EarlyBirdBadge
} from './RegistrationFormStyles';
import * as FaIcons from 'react-icons/fa';

// Razorpay types
declare global {
  interface Window {
    Razorpay: any;
  }
}

interface RazorpayOptions {
  key: string;
  amount: number;
  currency: string;
  name: string;
  description: string;
  order_id: string;
  handler: (response: RazorpayResponse) => void;
  prefill: {
    name: string;
    email: string;
    contact: string;
  };
  notes: {
    [key: string]: string;
  };
  theme: {
    color: string;
  };
  image?: string;
}

interface RazorpayResponse {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}

// Create a simple spinner component with animation
const spinAnimation = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

// Icon components using React.createElement to avoid TypeScript errors
const SpinnerIcon = styled.div`
  display: inline-block;
  animation: ${spinAnimation} 1s linear infinite;
  margin-right: 8px;
  svg {
    display: block;
  }
`;

const IconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  svg {
    display: block;
  }
`;

// Helper functions to render icons safely
const renderSpinner = (): React.ReactElement => {
  const IconComponent = FaSpinner as React.FC<IconBaseProps>;
  return (
    <SpinnerIcon>
      <IconComponent />
    </SpinnerIcon>
  );
};

const renderCheckIcon = (size = 16): React.ReactElement => {
  const IconComponent = FaCheckCircle as React.FC<IconBaseProps>;
  return (
    <IconWrapper>
      <IconComponent size={size} />
    </IconWrapper>
  );
};

const renderCreditCardIcon = (size = 16): React.ReactElement => {
  const IconComponent = FaCreditCard as React.FC<IconBaseProps>;
  return (
    <IconWrapper>
      <IconComponent size={size} />
    </IconWrapper>
  );
};

const renderRupeeIcon = (size = 16): React.ReactElement => {
  const IconComponent = FaRupeeSign as React.FC<IconBaseProps>;
  return (
    <IconWrapper>
      <IconComponent size={size} />
    </IconWrapper>
  );
};

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  studentClass: string;
  track: string;
  school: string;
  city: string;
}

interface PaymentData {
  orderId: string;
  paymentId: string;
  registrationId: string;
  amount: number;
  timestamp: string;
}

interface Confirmation {
  id: string;
  message: string;
}

interface PriceDetails {
  basePrice: number;
  total: number;
  displayBasePrice: string;
  displayTotal: string;
}

// Main Registration Form Component
const RegistrationForm = ({
  onClose = () => {},
  campName = 'IIITD-IC Summer Camp 2026',
  basePrice = 5000
}: {
  onClose?: () => void;
  campName?: string;
  basePrice?: number;
}) => {
  // State variables
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [formData, setFormData] = useState<FormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    studentClass: '',
    track: 'robotics',
    school: '',
    city: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [paymentProcessing, setPaymentProcessing] = useState<boolean>(false);
  const [paymentData, setPaymentData] = useState<PaymentData | null>(null);
  const [confirmation, setConfirmation] = useState<Confirmation>({
    id: '',
    message: ''
  });
  const [csrfToken, setCsrfToken] = useState<string>('');

  // Utility functions
  const generateCSRFToken = (): string => {
    return Math.random().toString(36).substring(2, 15);
  };

  const secureStore = (key: string, value: string): void => {
    try {
      localStorage.setItem(key, btoa(value)); // Simple base64 encoding
    } catch (e) {
      console.warn('localStorage not available, CSRF token not stored securely.');
    }
  };

  const secureRetrieve = (key: string): string | null => {
    try {
      const storedValue = localStorage.getItem(key);
      return storedValue ? atob(storedValue) : null;
    } catch (e) {
      console.warn('localStorage not available or value corrupted.');
      return null;
    }
  };

  // Generate CSRF token on component mount
  useEffect(() => {
    const token = generateCSRFToken();
    setCsrfToken(token);
    secureStore('razorpay_csrf_token', token);
    
    // Add Razorpay script
    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.async = true;
    document.body.appendChild(script);
    
    return () => {
      // Clean up the script when the component unmounts
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);
  
  // Handle form input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Calculate price: base + 18% GST
  const calculatePrice = (): PriceDetails => {
    const gst = Math.round(basePrice * 0.18);
    const total = basePrice + gst;

    const formatCurrency = (amount: number): string => {
      return amount.toLocaleString('en-IN');
    };

    return {
      basePrice,
      total,
      displayBasePrice: formatCurrency(basePrice),
      displayTotal: formatCurrency(total)
    };
  };

  // Verify Razorpay payment signature (now delegated to backend)
  const verifyPaymentSignature = async (paymentId: string, orderId: string, signature: string): Promise<boolean> => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const response = await fetch(`${apiUrl}/api/verify-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          razorpay_payment_id: paymentId,
          razorpay_order_id: orderId,
          razorpay_signature: signature
        }),
      });
      
      const data = await response.json();
      return data.success;
    } catch (error) {
      console.error('Error verifying payment signature:', error);
      return false;
    }
  };

  // Validate form data
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[6-9]\d{9}$/.test(formData.phone)) {
      newErrors.phone = 'Phone number must be 10 digits starting with 6-9';
    }
    
    if (!formData.studentClass) {
      newErrors.studentClass = 'Class is required';
    }
    
    if (!formData.track) {
      newErrors.track = 'Track selection is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit registration data to Google Sheets
  const sendToGoogleSheets = async (status: string): Promise<boolean> => {
    try {
      const formattedTrack = formData.track === 'robotics' ? 'Track 1: Robotics + IoT + AI' : 'Track 2: Drone Tech + Aero Modelling';
      const scriptURL = 'https://script.google.com/macros/s/AKfycbwEBsAxLUORV48jIF2WkcZ0bplJc0EHPA8nB2muVlMnkXrZifTIoxSE8J5wyvUBlEgF8A/exec';
      
      // Prepare parameters for URL - matching Google Script parameter names exactly
      const params = [
        `firstName=${encodeURIComponent(formData.firstName)}`,
        `lastName=${encodeURIComponent(formData.lastName)}`,
        `email=${encodeURIComponent(formData.email)}`,
        `phone=${encodeURIComponent(formData.phone)}`,
        `class=${encodeURIComponent(formData.studentClass)}`,
        `track=${encodeURIComponent(formattedTrack)}`,
        `school=${encodeURIComponent(formData.school || '')}`,
        `city=${encodeURIComponent(formData.city || '')}`,
        `status=${encodeURIComponent(status)}`,
        `timestamp=${encodeURIComponent(new Date().toISOString())}`,
        `nocache=${new Date().getTime()}`
      ];

      // Add payment data if available
      if (paymentData && status === 'Payment Completed') {
        params.push(
          `paymentId=${encodeURIComponent(paymentData.paymentId)}`,
          `orderId=${encodeURIComponent(paymentData.orderId)}`,
          `registrationId=${encodeURIComponent(paymentData.registrationId)}`,
          `amount=${encodeURIComponent(paymentData.amount.toString())}`,
          `paymentTimestamp=${encodeURIComponent(paymentData.timestamp)}`
        );
      } else {
        // Add empty payment fields for form submission entries
        params.push(
          `paymentId=`,
          `orderId=`,
          `registrationId=`,
          `amount=`,
          `paymentTimestamp=`
        );
      }
      
      const queryString = params.join('&');
      
      // Create image ping (most reliable cross-domain method)
      const img = new Image();
      img.style.display = 'none';
      img.onload = () => {};
      img.onerror = () => {};
      img.src = `${scriptURL}?${queryString}`;
      document.body.appendChild(img);
      
      // Remove after a short delay
      setTimeout(() => {
        if (document.body.contains(img)) document.body.removeChild(img);
      }, 3000);
      
      console.log(`Sending data to Google Sheets: ${scriptURL}?${queryString}`);
      return true;
    } catch (error) {
      console.error(`Error sending ${status} data:`, error);
      return false;
    }
  };

  // Submit registration data to Google Sheets with payment data
  const sendToGoogleSheetsWithPayment = async (status: string, paymentInfo: PaymentData): Promise<boolean> => {
    try {
      const formattedTrack = formData.track === 'robotics' ? 'Track 1: Robotics + IoT + AI' : 'Track 2: Drone Tech + Aero Modelling';
      const scriptURL = 'https://script.google.com/macros/s/AKfycbwEBsAxLUORV48jIF2WkcZ0bplJc0EHPA8nB2muVlMnkXrZifTIoxSE8J5wyvUBlEgF8A/exec';
      
      // Prepare parameters for URL - matching Google Script parameter names exactly
      const params = [
        `firstName=${encodeURIComponent(formData.firstName)}`,
        `lastName=${encodeURIComponent(formData.lastName)}`,
        `email=${encodeURIComponent(formData.email)}`,
        `phone=${encodeURIComponent(formData.phone)}`,
        `class=${encodeURIComponent(formData.studentClass)}`,
        `track=${encodeURIComponent(formattedTrack)}`,
        `school=${encodeURIComponent(formData.school || '')}`,
        `city=${encodeURIComponent(formData.city || '')}`,
        `status=${encodeURIComponent(status)}`,
        `timestamp=${encodeURIComponent(new Date().toISOString())}`,
        `nocache=${new Date().getTime()}`
      ];

      // Add payment data
      params.push(
        `paymentId=${encodeURIComponent(paymentInfo.paymentId)}`,
        `orderId=${encodeURIComponent(paymentInfo.orderId)}`,
        `registrationId=${encodeURIComponent(paymentInfo.registrationId)}`,
        `amount=${encodeURIComponent(paymentInfo.amount.toString())}`,
        `paymentTimestamp=${encodeURIComponent(paymentInfo.timestamp)}`
      );
      
      const queryString = params.join('&');
      
      // Create image ping (most reliable cross-domain method)
      const img = new Image();
      img.style.display = 'none';
      img.onload = () => {};
      img.onerror = () => {};
      img.src = `${scriptURL}?${queryString}`;
      
      // Add to DOM temporarily
      document.body.appendChild(img);
      
      // Remove after a short delay
      setTimeout(() => {
        if (img.parentNode) {
          img.parentNode.removeChild(img);
        }
      }, 1000);
      
      console.log('Data sent to Google Sheets:', queryString);
      return true;
    } catch (error) {
      console.error('Error sending to Google Sheets:', error);
      return false;
    }
  };

  // Initialize Razorpay payment
  const initializePayment = async (): Promise<void> => {
    setPaymentProcessing(true);
    setErrors({});
    
    try {
      // Calculate price
      const priceDetails = calculatePrice();
      
      // Generate unique IDs
      const receiptId = 'receipt_' + Date.now();
      const orderRef = `order_ref_${Date.now()}_${Math.random().toString(36).substring(2, 6)}`;
      
      // Prepare notes for the order
      const notes = {
        orderRef: orderRef,
        studentClass: formData.studentClass,
        track: formData.track,
        totalAmount: priceDetails.total.toString(),
        registrationTime: new Date().toISOString(),
        studentId: `STUDENT-${new Date().getTime().toString().slice(-6)}`
      };
      
      // Call backend API to create Razorpay order
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      console.log('Creating order with amount:', priceDetails.total);
      
      const response = await fetch(`${apiUrl}/api/create-order`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          amount: priceDetails.total,
          receipt: receiptId,
          notes: notes
        }),
      });
      
      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.error || 'Failed to create order. Please try again.');
      }
      
      const order = data.order;
      console.log('Order created:', order);
      
      if (!order || !order.id) {
        throw new Error('Invalid order response from server');
      }
      
      // Configure Razorpay payment
      const options: RazorpayOptions = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID || '',
        amount: order.amount,
        currency: order.currency,
        name: campName,
        description: `${campName} Registration - ${formData.track === 'robotics' ? 'Robotics + IoT + AI' : 'Drone Tech + Aero Modelling'} Track`,
        order_id: order.id,
        prefill: {
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          contact: `91${formData.phone}`
        },
        notes: notes,
        theme: {
          color: '#06b6d4'
        },
        handler: async function(response: RazorpayResponse) {
          try {
            // Verify the payment with backend
            const verificationResponse = await fetch(`${apiUrl}/api/verify-payment`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_order_id: response.razorpay_order_id,
                razorpay_signature: response.razorpay_signature
              }),
            });
            
            const verificationData = await verificationResponse.json();
            
            if (verificationData.success) {
              // Payment verified successfully
              const registrationId = `REG-${Date.now()}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;
              
              const paymentInfo = {
                orderId: response.razorpay_order_id,
                paymentId: response.razorpay_payment_id,
                registrationId: registrationId,
                amount: priceDetails.total,
                timestamp: new Date().toISOString()
              };
              
              setPaymentData(paymentInfo);
              
              // Send completed payment data to Google Sheets with payment info directly
              await sendToGoogleSheetsWithPayment('Payment Completed', paymentInfo);
              
              setConfirmation({
                id: `TECHCAMP-${Date.now()}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`,
                message: `Your registration is complete and payment has been received. We look forward to seeing you at ${campName}!`
              });
              
              setCurrentStep(3);
            } else {
              throw new Error(verificationData.error || 'Payment verification failed');
            }
          } catch (error: any) {
            console.error('Payment verification error:', error);
            setErrors({ payment: error.message || 'Payment verification failed. Please try again.' });
          } finally {
            setPaymentProcessing(false);
          }
        }
      };
      
      const razorpay = new window.Razorpay(options);
      
      razorpay.on('payment.failed', function(response: any) {
        console.error('Payment failed:', response.error);
        setErrors({ 
          payment: response.error.description || 'Payment failed. Please try again.' 
        });
        setPaymentProcessing(false);
      });
      
      razorpay.open();
      
    } catch (error: any) {
      console.error('Error initializing payment:', error);
      setErrors({ payment: error.message || 'An error occurred during payment. Please try again.' });
      setPaymentProcessing(false);
    }
  };

// Handle form submission (Step 1)
const handleSubmit = async (e: React.FormEvent): Promise<void> => {
  e.preventDefault();
  setErrors({});

  if (validate()) {
    setLoading(true);

    try {
      // Remove this line to prevent first entry
      // await sendToGoogleSheets('Form Submitted');

      // Move to payment step
      setTimeout(() => {
        setLoading(false);
        setCurrentStep(2);
      }, 1000);
    } catch (error: any) {
      console.error('Error in registration step:', error);
      setErrors({ submit: 'There was an error processing your registration. Please try again.' });
      setLoading(false);
    }
  }
};

// Render Step 1: Registration Form
const renderRegistrationForm = (): React.ReactElement => (
  <FormContainer
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
  >
    <PaymentSteps>
      <Step active={true} completed={false}>
        <div className="step-number">1</div>
        <div className="step-label">Your Details</div>
      </Step>
      <Step active={false} completed={false}>
        <div className="step-number">2</div>
        <div className="step-label">Payment</div>
      </Step>
      <Step active={false} completed={false}>
        <div className="step-numbercd server && npm start">3</div>
        <div className="step-label">Confirmation</div>
      </Step>
    </PaymentSteps>

    <FormTitle>
      <span>{campName}</span> Registration
    </FormTitle>

    {errors.submit && (
      <ErrorMessage style={{
        padding: '10px',
        marginBottom: '15px',
        background: 'rgba(220, 38, 38, 0.1)',
        borderRadius: 'var(--rounded, 1rem)',
        textAlign: 'center'
      }}>
        {errors.submit}
      </ErrorMessage>
    )}

    <form onSubmit={handleSubmit}>
      <FormRow>
        <FormGroup>
          <Label htmlFor="firstName">First Name</Label>
          <Input
            type="text"
            id="firstName"
            name="firstName"
            placeholder="Enter first name"
            value={formData.firstName}
            onChange={handleChange}
          />
          {errors.firstName && <ErrorMessage>{errors.firstName}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="lastName">Last Name</Label>
          <Input
            type="text"
            id="lastName"
            name="lastName"
            placeholder="Enter last name"
            value={formData.lastName}
            onChange={handleChange}
          />
          {errors.lastName && <ErrorMessage>{errors.lastName}</ErrorMessage>}
        </FormGroup>
      </FormRow>

      <FormRow>
        <FormGroup>
          <Label htmlFor="email">Email</Label>
          <Input
            type="email"
            id="email"
            name="email"
            placeholder="Enter email"
            value={formData.email}
            onChange={handleChange}
          />
          {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="phone">Phone Number</Label>
          <PhoneInputWrapper>
            <PhonePrefix>+91</PhonePrefix>
            <Input
              type="tel"
              id="phone"
              name="phone"
              placeholder="Enter phone number"
              value={formData.phone}
              onChange={handleChange}
              style={{ paddingLeft: '50px' }}
            />
          </PhoneInputWrapper>
          {errors.phone && <ErrorMessage>{errors.phone}</ErrorMessage>}
        </FormGroup>
      </FormRow>

      <FormRow>
        <FormGroup>
          <Label htmlFor="studentClass">Class</Label>
          <Select
            id="studentClass"
            name="studentClass"
            value={formData.studentClass}
            onChange={handleChange}
          >
            <option value="">Select class</option>
            {[4, 5, 6, 7, 8, 9, 10, 11, 12].map((classNum) => (
              <option key={classNum} value={classNum}>{classNum}th Standard</option>
            ))}
          </Select>
          {errors.studentClass && <ErrorMessage>{errors.studentClass}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label htmlFor="track">Preferred Track</Label>
          <Select
            id="track"
            name="track"
            value={formData.track}
            onChange={handleChange}
          >
            <option value="">Select track</option>
            <option value="robotics">Track 1: Robotics + IoT + AI</option>
            <option value="drones">Track 2: Drone Tech + Aero Modelling</option>
          </Select>
          {errors.track && <ErrorMessage>{errors.track}</ErrorMessage>}
        </FormGroup>
      </FormRow>

      <Button
        type="submit"
        disabled={loading}
      >
        {loading ? (
          <>
            {renderSpinner()} Processing...
          </>
        ) : 'Continue to Payment'}
      </Button>
    </form>
  </FormContainer>
);

// Render Step 2: Payment Screen
const renderPaymentScreen = (): React.ReactElement => {
  const price = calculatePrice();

  return (
    <FormContainer 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <PaymentSteps>
        <Step active={false} completed={true}>
          <div className="step-number">{renderCheckIcon(16)}</div>
          <div className="step-label">Your Details</div>
        </Step>
        <Step active={true} completed={false}>
          <div className="step-number">2</div>
          <div className="step-label">Payment</div>
        </Step>
        <Step active={false} completed={false}>
          <div className="step-number">3</div>
          <div className="step-label">Confirmation</div>
        </Step>
      </PaymentSteps>
      
      <FormTitle>
        <span>{campName}</span> Payment
      </FormTitle>
      
      <PaymentContainer>
        <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
          <h3>{campName} — With Kit Included</h3>
        </div>

        <PriceList>
          <PriceItem>
            <span>Course Fee</span>
            <span>₹{price.displayBasePrice}</span>
          </PriceItem>
          <PriceItem>
            <span>GST (18%)</span>
            <span>₹{(price.total - price.basePrice).toLocaleString('en-IN')}</span>
          </PriceItem>
          <PriceItem style={{ fontWeight: 'bold', fontSize: '1.2em', borderTop: '1px solid #e5e7eb', paddingTop: '0.75rem', marginTop: '0.5rem' }}>
            <span>Total</span>
            <span>₹{price.displayTotal}</span>
          </PriceItem>
        </PriceList>
        
        {errors.payment && (
          <ErrorMessage style={{ marginBottom: '1rem' }}>
            {errors.payment}
          </ErrorMessage>
        )}
        
        <PaymentButton 
          onClick={initializePayment}
          disabled={paymentProcessing}
        >
          {paymentProcessing ? (
            <>
              {renderSpinner()}
              Processing Payment...
            </>
          ) : (
            <>
              {renderCreditCardIcon(20)}
              Pay ₹{price.displayTotal}
            </>
          )}
        </PaymentButton>
        
        <Button 
          type="button" 
          onClick={() => setCurrentStep(1)}
          style={{ 
            marginTop: '1rem',
            background: 'transparent',
            color: '#666',
            border: '1px solid #ddd'
          }}
        >
          Back to Details
        </Button>
      </PaymentContainer>
    </FormContainer>
  );
};
  
// Render Step 3: Confirmation Screen
const renderConfirmationScreen = (): React.ReactElement => (
  <FormContainer 
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5 }}
  >
    <PaymentSteps>
      <Step active={false} completed={true}>
        <div className="step-number">{renderCheckIcon(16)}</div>
        <div className="step-label">Your Details</div>
      </Step>
      <Step active={false} completed={true}>
        <div className="step-number">{renderCheckIcon(16)}</div>
        <div className="step-label">Payment</div>
      </Step>
      <Step active={true} completed={true}>
        <div className="step-number">{renderCheckIcon(16)}</div>
        <div className="step-label">Confirmation</div>
      </Step>
    </PaymentSteps>
    
    <ConfirmationMessage>
      {renderCheckIcon(48)}
      <h3>{campName} Registration Complete!</h3>
      <p>{confirmation.message}</p>
      
      <div style={{ margin: '20px 0', textAlign: 'left', background: 'rgba(16, 185, 129, 0.05)', padding: '15px', borderRadius: 'var(--rounded, 1rem)' }}>
        <p style={{ margin: '5px 0', color: '#666' }}>
          <strong>Payment ID:</strong> {paymentData?.paymentId}
        </p>
        <p style={{ margin: '5px 0', color: '#666' }}>
          <strong>Order ID:</strong> {paymentData?.orderId}
        </p>
        <p style={{ margin: '5px 0', color: '#666' }}>
          <strong>Amount Paid:</strong> ₹{paymentData?.amount.toLocaleString()}
        </p>
      </div>
      
      <p>Your registration confirmation number:</p>
      <div className="confirmation-number">{confirmation.id}</div>
      
      <p style={{ fontSize: '0.9rem' }}>
        A confirmation email has been sent to <strong>{formData.email}</strong> with all the details.
      </p>
      
      <Button onClick={onClose}>
        Close
      </Button>
    </ConfirmationMessage>
  </FormContainer>
);
  
// Render the appropriate step
const renderStep = (): React.ReactElement => {
  switch (currentStep) {
    case 1:
      return renderRegistrationForm();
    case 2:
      return renderPaymentScreen();
    case 3:
      return renderConfirmationScreen();
    default:
      return renderRegistrationForm(); // Or handle error appropriately
  }
};


return renderStep();
};

export default RegistrationForm;
